import { CarbonIconType } from '@carbon/icons-react';
import { ReactElement } from 'react';

/*
    This file is for storing public, re-usable types/enums/interfaces that are used in multiple components/pages.
    Specific types e.g. component prop types should exist in the component/page file itself.
*/


/*
    Enums: immutable, constant values (usually strings) that are used in multiple places; often reflected in the DB.
*/
export enum DeviceStatus {
    Online = 'In Transit',
    Idling = 'Idling',
    Stopped = 'Stopped',
    Offline = 'Offline',
}
export enum DeviceTimelineBlockType {
    Idle = 'idle',
    Moving = 'moving',
    Stopped = 'stopped',
}
export enum DeviceType {
    Tracker = 0,
    Dvr = 1,
}
export enum DeviceTimelineStatus {
    Idle = 'idle',
    Moving = 'moving',
    Stopped = 'offline',
}
export enum LicenceType {
    A = 'Category A',
    A1 = 'Category A1',
    A2 = 'Category A2',
    AM = 'Category AM',
    B1 = 'Category B1',
    BE = 'Category BE',
    BAuto = 'Category B Auto',
    C = 'Category C',
    C1 = 'Category C1',
    C1E = 'Category C1E',
    CE = 'Category CE',
    D = 'Category D',
    D1 = 'Category D1',
    D1E = 'Category D1E',
    DE = 'Category DE',
    P = 'Category P',
    Q = 'Category Q',
}
export enum MarkerType {
    Asset = 'asset',
    PathStart = 'path_start',
    PathEnd = 'path_end',
    Event = 'event',
    SearchResult = 'search_result',
}
export enum PathMarkerType {
    Start = 'start',
    End = 'end',
}
export enum PopupType {
    Asset = 'asset',
    Event = 'event',
}
export enum UnitOfMeasurement {
    Metric = 'metric',
    BritishImperial = 'british-imperial',
}
export enum UnitOfDistance {
    Metric = 'km',
    BritishImperial = 'miles',
}
export enum UnitOfSpeed {
    Metric = 'kph',
    BritishImperial = 'mph',
}
export enum EventIconType {
    Alert = 'alert',
    Api = 'api',
    Bell = 'bell',
    Environment = 'environment',
    Flag = 'flag',
}
export enum DvrHealthStatus {
    NoData = 'no_data',
    Healthy = 'healthy',
    Warning = 'warning',
    Critical = 'critical',
}
export enum MapMode {
    Live = 'live',
    Historic = 'historic',
}
export enum MapToolbarButtonType {
    ToggleAssetMarkerNames,
    ToggleTrafficOverlay,
    GeofencesMenu,
    ToggleLocationSearch,
    ToggleSatelliteView,
    HeatmapsMenu,
    ToggleVideoSearch,
}
export enum IcarCommonAlarmType { // based on https://awsvideo.fleetclear.com/kpi/
    CustomAlarm = 1,
    EmergencyButtonAlarm = 2,
    VibrationAlarm = 3,
    VideoLostAlarm = 4,
    CameraBlockingAlarm = 5,
    IllegalDoorOpeningAlarm = 6,
    ThreePasswordErrorAlarm = 7,
    IllegalIgnitionAlarm = 8,
    TemperatureAlarm = 9,
    HardDiskErrorAlarm = 10,
    OverSpeedAlarm = 11,
    CrossBorderAlarm = 12,
    AbnormalSwitchDoorAlarm = 13,
    ParkingTooLongAlarm = 14,
    MotionDetectionAlarm = 15,
    AccOnAlarm = 16,
    MdvrOnline = 17,
    GpsSignalLostAlarmStart = 18,
    Io_1AlarmStart = 19,
    Io_2AlarmStart = 20,
    Io_3AlarmStart = 21,
    Io_4AlarmStart = 22,
    Io_5AlarmStart = 23,
    Io_6AlarmStart = 24,
    Io_7AlarmStart = 25,
    Io_8AlarmStart = 26,
    AlarmStartInTheArea = 27,
    AlarmStartOutOfTheArea = 28,
    HighSpeedAlarmStartInTheArea = 29,
    HighSpeedAlarmStartOutOfTheArea = 30,
    LowSpeedAlarmStartInTheArea = 31,
    LowSpeedAlarmStartOutOfTheArea = 32,
    ParkAlarmStartInTheArea = 33,
    ParkAlarmStartOutOfTheArea = 34,
    FireAlarm = 35,
    RobberAlarm = 36,
    SchedulingTaskCompletion = 37,
    ImageUploadCompleted = 38,
    HardDisk1DoesNotExist = 39,
    HardDisK2DoesNotExist = 40,
    Io_9Alarm = 41,
    Io_10Alarm = 42,
    Io_11Alarm = 43,
    Io_12Alarm = 44,
    GpsInvalid = 45,
}
export enum SliderUnitType {
    Seconds = 'seconds',
    Minutes = 'minutes',
}
export enum SmartWidgetDropdownOptions {
    Inspect = 'see data in table',
    Edit = 'edit widget',
    Delete = 'delete',
}
export enum UserRole {
    User = 'user',
    Driver = 'driver',
    Manager = 'manager',
    Admin = 'admin',
    SuperAdmin = 'super-admin',
    SalesAdmin = 'sales-admin',
}


/*
    Interfaces: reusable data structures that are used in multiple places. Helps to enforce data shape and avoid bugs.
*/
export interface Asset {
    id: number;
    name: string;
    division_id: number;
    driver_id: number | null;
}
export interface AssetMarkerPoint {
    record_id: number;
    date: string; // "YYYY-MM-DD HH:MM:SS"
    time: string; // "HH:MM:SS"
    time_interval: number;
    UTC_time_interval: number;
    lat: number;
    lng: number;
    angle: number;
    is_idle: boolean;
}
export interface AssetMarkerData {
    title: string;
    imei: string,
    registration: string,
    online: number; // 0 or 1
    time: string, // "YYYY-MM-DD HH:MM:SS"
    lat: number;
    lng: number;
    asset_id: number;
    device_status: number; // 0 or 1
    division: string;
    division_id: number;
    division_color: string;
    dvr_devices: { 
        imei: string; 
        asset_id: number; 
    }[];
    devices: []; // always empty?
    markers: AssetMarkerPoint[];
    driver_name: string;
    driver_id: number;
}
export interface AssetPopupContentData { // deprecated
    type: PopupType.Asset;
    assetId?: string | null;
    date?: string | null;
}
export interface AvlKey {
    description: string;
    value: string;
}
export interface Channel {
    channel: string;
    label: string;
}
export interface DeviceTimelineData {
    timelineSegments: DeviceTimelineSegment[];
    geofenceTimelineSegments: GeofenceTimelineSegment[];
}
export interface DeviceTimelineSegment {
    start: number;
    start_time: string;
    end: number;
    end_time: string;
    location: string;
    status: DeviceTimelineStatus;
    latitude: string;
    longitude: string;
    journey_id: number;
    journey_start: string;
    journey_end: string;
    journey_name: string;
}
export interface Division {
    id: string,
    name: string,
}
export interface DriverStatus {
    title: string;
    key: string;
}
export interface DvrChannelLink {
    channel: number;
    device: string;
    duration: number;
    end_time: string;
    id: string;
    start_time: string;
    url: string;
    video_api: string;
}
export interface DvrStatusData {
    deviceId: number,
    imei: string,
    status: 0 | 1,
}
export interface Event {
    date: string;
    event_id: string;
    event_title: string;
    event_icon: string;
    device_id: string;
    asset_name: string;
}
export interface EventType {
    id: number;
    key: string;
}
export interface EventPopupContentData {
    type: PopupType.Event;
    speed: number;
    eventName: string;
    time: string;
    driverId: number;
    driverName: string;
    location: Location;
    avlKeys: AvlKey[];
}
export interface GeofencePoint {
    id: string,
    lat: string,
    lng: string,
}
export interface Geofence {
    divisions: Division[];
    id: number;
    name: string;
    trigger_points: GeofencePoint[];
    type: number;
}
export interface GeofenceTimelineSegment {
    start: number;
    start_time: string;
    end: number;
    end_time: string;
    geofence_names: string[];
}
export interface HeatmapEvent {
    events: Event[];
    latitude: number;
    longitude: number;
    record_id: number;
    registration: string;
    title: string;
    weight: number;
}
export interface LatLng {
    lat: number;
    lng: number;
}
export interface MapToolbarButtonProps {
    key: MapToolbarButtonType;
    icon: ReactElement<any, any>;
    tooltip: string;
    menuOptions?: {
        key: string, 
        label: string
    }[];
    onClick?: React.MouseEventHandler<HTMLElement>;
}
export interface LicenceTypes {
    create_time: string;
    deleted: number;
    id: number;
    status: number;
    title: LicenceType;
    update_time: string;
}
export interface Location { // TODO: replace all lat/lng with this type
    lat: number;
    lng: number;
    address?: string;
}
export interface MarkerDataOld { // DEPRECATED
    type: MarkerType;
    location: Location;
    divisionColor?: string;
    angle?: number;
    label?: string;
    deviceStatus?: string;
    cachedAvlInfo?: string;
    speed?: string;
    icon?: string;
    popupData?: AssetPopupContentData | EventPopupContentData | null;
}
export interface PathSegmentData {
    location: Location;
    eventType: number;
    icon: string;
    datetime: string;
    popupData?: EventPopupContentData | null;
}
export interface SnailTrailPoint { // could later be merged with AssetMarkerData but requires significant API changes because live route returns different point shape to historic route
    angle: number;
    date: string; // "YYYY-MM-DD HH:MM:SS"
    device_id: number;
    event_id: number;
    event_type: number;
    event_icon: string;
    event_label: string;
    lat: string;
    lng: string;
    record_id: number;
    speed: number;
    speed_unit: UnitOfSpeed;
    time: string; // "HH:MM:SS"
    time_interval: number;
}
export interface Tag {
    company_id: number;
    created_at: string;
    id: number;
    key: string;
    name: string;
    updated_at: string;
    assets: number;
}
export interface User {
    userCompany: UserCompany;
}
export interface UserCompany {
    current_alert: string;
}
export interface VideoDownloadStatus {
    action?: ReactElement,
    channel?: Number,
    channel_index?: Number,
    channel_name?: string,
    file_length?: string,
    file_size?: string, // TODO: camelcase?
    status?: any, // TODO: determine type
    stitched_video_id?: string,
    download_queue_id?: string,
}
export interface VideoSearchResponse {
    info: {
        id: number;
        device_status: string;
        lat: string;
        lng: string;
        angle: number;
        name: string;
        division_color: string;
        cached_avl_info: string;
        speed: string;
        user_id: number;
        full_name: string;
    };
    locations: {
        lat: string;
        lng: string;
        event_type: string;
        icon: string;
        time: string;
        event_key: string;
        speed: number;
        show_in_events: boolean | null;
        location_data: AvlKey[];
    }[];
}
